import { useState } from 'react';
import {
  usePatchIncidentSite, useSweepSite, useCreateIncidentPerson,
  useExcludeSite, useDeleteIncidentSite, useSetSiteSectors,
  type IncidentSite,
} from '../api';
import { CONTACT_RESULTS, HAZARDS, SITE_KINDS, STRUCTURE, formatTerm } from '../incidentVocab';

/** Site-level entry: the sweep fast path, ATC-20 placard, hazards, occupants. */
export default function SiteControls({ site, incidentId, layer, sectors }: {
  site: IncidentSite;
  incidentId: string;
  /** Which sector layer the board is currently grouped by. */
  layer: string;
  sectors: string[];
}) {
  const patch = usePatchIncidentSite();
  const sweep = useSweepSite();
  const exclude = useExcludeSite();
  const setSectors = useSetSiteSectors();
  const del = useDeleteIncidentSite();
  const [addingPerson, setAddingPerson] = useState(false);

  const set = (body: Parameters<typeof patch.mutate>[0]['body']) =>
    patch.mutate({ incidentId, id: site.id, body });

  const toggleHazard = (h: string) =>
    set({ hazards: site.hazards.includes(h)
      ? site.hazards.filter((x) => x !== h)
      : [...site.hazards, h] });

  return (
    <div className="space-y-2 border-t border-slate-100 dark:border-slate-800 pt-2">
      {/* The overwhelmingly common outcome: knock, everyone's fine. Making this
          one button rather than one tap per occupant is the difference between
          a board that keeps up with a sweep and one that falls behind. */}
      <div className="flex flex-wrap gap-1">
        <button
          onClick={() => sweep.mutate({ incidentId, id: site.id })}
          disabled={sweep.isPending || site.people.length === 0}
          className="rounded bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-40 dark:bg-emerald-500"
        >
          {sweep.isPending ? 'Marking…' : 'All accounted'}
        </button>
        <button
          onClick={() => sweep.mutate({ incidentId, id: site.id, accounted: 'unknown', contact_result: 'no_answer' })}
          disabled={sweep.isPending}
          className="rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-xs hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          No answer
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Row label="This place is a">
          <select
            value={site.site_kind}
            onChange={(e) => set({ site_kind: e.target.value as IncidentSite['site_kind'] })}
            className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1.5 py-0.5 text-xs"
          >
            {SITE_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
        </Row>
        {site.site_kind === 'shelter' && (
          <Row label="Capacity">
            <input
              type="number" min={0}
              defaultValue={site.capacity ?? ''}
              onBlur={(e) => {
                const v = e.target.value === '' ? null : Number(e.target.value);
                if (v !== site.capacity) set({ capacity: v });
              }}
              placeholder="how many it holds"
              className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1.5 py-0.5 text-xs"
            />
          </Row>
        )}
      </div>

      <Row label="At the door">
        <select
          value={site.contact_result}
          onChange={(e) => set({ contact_result: e.target.value as IncidentSite['contact_result'] })}
          className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1.5 py-0.5 text-xs"
        >
          {CONTACT_RESULTS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </Row>

      <Row label="Structure (ATC-20)">
        <div className="flex flex-wrap gap-1">
          {STRUCTURE.map((s) => (
            <button
              key={s.value}
              onClick={() => set({ structure: s.value })}
              className={`rounded px-1.5 py-0.5 text-xs ${
                site.structure === s.value
                  ? s.tone
                  : 'border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </Row>

      <Row label="Hazards">
        <div className="flex flex-wrap gap-1">
          {HAZARDS.map((h) => (
            <button
              key={h}
              onClick={() => toggleHazard(h)}
              className={`rounded px-1.5 py-0.5 text-xs ${
                site.hazards.includes(h)
                  ? 'bg-amber-500 text-white'
                  : 'border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              {formatTerm(h)}
            </button>
          ))}
        </div>
      </Row>

      <div className="grid grid-cols-2 gap-2">
        <Row label={`Sector (${layer})`}>
          <input
            list="incident-sectors"
            key={`${site.id}-${layer}`}
            defaultValue={site.sectors?.[layer] ?? ''}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v !== (site.sectors?.[layer] ?? '')) {
                setSectors.mutate({ incidentId, id: site.id, sectors: { [layer]: v } });
              }
            }}
            placeholder="Block 3"
            className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1.5 py-0.5 text-xs"
          />
          {/* Offering back the spellings already in use is what stops
              "3300 E", "3300E", and "3300 e" becoming three sectors at 2am. */}
          <datalist id="incident-sectors">
            {sectors.map((s) => <option key={s} value={s} />)}
          </datalist>
        </Row>
        <Row label="People on site">
          <input
            type="number"
            min={0}
            defaultValue={site.occupants_present ?? ''}
            onBlur={(e) => {
              const v = e.target.value === '' ? null : Number(e.target.value);
              if (v !== site.occupants_present) set({ occupants_present: v });
            }}
            placeholder="count"
            className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1.5 py-0.5 text-xs"
          />
        </Row>
      </div>

      <Row label="Site notes">
        <textarea
          defaultValue={site.notes ?? ''}
          onBlur={(e) => {
            const v = e.target.value.trim() || null;
            if (v !== (site.notes ?? null)) set({ notes: v });
          }}
          rows={2}
          className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1.5 py-0.5 text-xs"
        />
      </Row>

      {addingPerson
        ? <AddPersonForm incidentId={incidentId} siteId={site.id} onDone={() => setAddingPerson(false)} />
        : (
          <button
            onClick={() => setAddingPerson(true)}
            className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
          >
            + Add person at this address
          </button>
        )}

      {/* Skip vs delete are genuinely different. Skipping keeps the row, so
          the idempotent seed will not re-create it and anything recorded here
          survives if it comes back. Delete is for rows that are simply wrong —
          and a seeded household WILL return on the next seed. */}
      <div className="flex items-center gap-2 border-t border-slate-100 dark:border-slate-800 pt-2">
        <button
          onClick={() => exclude.mutate({ incidentId, id: site.id, excluded: true })}
          disabled={exclude.isPending}
          className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
          title="Take this address off the board and out of the counts. Reversible, and survives a re-seed."
        >
          Skip — not my area
        </button>
        <button
          onClick={() => {
            const seeded = site.household_id !== null;
            const msg = `Delete "${site.label}" and its ${site.people.length} occupant(s)?`
              + (seeded
                  ? '\n\nThis address came from the ward roster, so the next seed will add it back. To remove it for good, use Skip instead.'
                  : '\n\nThis was added by hand, so it will not come back.');
            if (window.confirm(msg)) del.mutate({ incidentId, id: site.id });
          }}
          disabled={del.isPending}
          className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs text-slate-500 hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-950 dark:hover:text-rose-300"
        >
          Delete
        </button>
      </div>

      {(patch.error || sweep.error || exclude.error || del.error || setSectors.error) && (
        <p className="rounded bg-rose-50 dark:bg-rose-950 px-1.5 py-1 text-xs text-rose-700 dark:text-rose-300">
          {((patch.error || sweep.error || exclude.error || del.error || setSectors.error) as Error).message}
        </p>
      )}
    </div>
  );
}

function AddPersonForm({ incidentId, siteId, onDone }: {
  incidentId: string; siteId: string; onDone: () => void;
}) {
  const create = useCreateIncidentPerson();
  const [name, setName] = useState('');
  const [age, setAge] = useState('');

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return;
        create.mutate(
          { incidentId, site_id: siteId, name: name.trim(), age: age ? Number(age) : null },
          { onSuccess: onDone },
        );
      }}
      className="flex gap-1"
    >
      <input
        autoFocus value={name} onChange={(e) => setName(e.target.value)}
        placeholder="Name, or a description"
        className="flex-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1.5 py-0.5 text-xs"
      />
      <input
        value={age} onChange={(e) => setAge(e.target.value)} type="number" min={0}
        placeholder="age"
        className="w-14 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1.5 py-0.5 text-xs"
      />
      <button type="submit" disabled={create.isPending || !name.trim()}
        className="rounded bg-slate-800 dark:bg-slate-200 px-2 text-xs text-white dark:text-slate-900 disabled:opacity-40">
        Add
      </button>
      <button type="button" onClick={onDone} className="text-xs text-slate-400">✕</button>
    </form>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-0.5 text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">{label}</div>
      {children}
    </div>
  );
}
